import { tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';

import { environment } from '../../../environments/environment';

import { RequestService } from './requestService';
import { User } from '../classes/user';
import { Answer } from '../classes/answer';
import { Application } from '../classes/application';

@Injectable()
export class UserService {
  private _apiEndpoint = environment.apiUrl + 'users/'; // URL to web api

  constructor(
    private http: HttpClient,
    private _router: Router,
    private _requestService: RequestService
  ) {}

  public getCurrentUser() {
    return this.http.get<User>(this._apiEndpoint + 'current-user/');
  }

  public login(username: String, password: String) {
    const body = JSON.stringify({ username, password });

    return this.http.post<User>(this._apiEndpoint + 'token-auth/', body).pipe(
      tap(res => {
        // Stores auth token generated by server in local storage
        localStorage.setItem('id_token', res.token);
      })
    );
  }

  public logout() {
    // clears localstorage on logout - prevents values persisting if logging in to different account if not logged out.
    localStorage.clear();

    // Invalidates all cached data
    this._requestService.invalidateAll();

    this._router.navigate(['/']);
  }

  public register(user: User) {
    const body = JSON.stringify(user);

    return this.http.post<User>(this._apiEndpoint, body);
  }

  public updateProfilePhoto(user) {
    return this.http.put<User>(this._apiEndpoint + user.id + '/', user);
  }

  private isUrl(s) {
    const regexp = /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;
    return regexp.test(s);
  }

  public update(user: User) {
    const body = JSON.stringify(user);
    return this.http.put<User>(this._apiEndpoint + user.id + '/', body);
  }

  public updatePassword(password: string) {
    const body = JSON.stringify({ password });

    return this.http.put(this._apiEndpoint + 'update-password/', body);
  }

  public verifyToken() {
    const body = { token: localStorage.getItem('id_token') };
    return this.http.post(this._apiEndpoint + 'token-verify/', body);
  }

  public resetPassword(password: string, token: string) {
    const body = JSON.stringify({ password, token });

    return this.http.post(this._apiEndpoint + 'reset-password/', body);
  }

  public doesUserExist(username: string) {
    const myParameters = new HttpParams();
    myParameters.append('username', username);
    return this.http.get(this._apiEndpoint + 'exists/', {
      params: myParameters,
    });
  }

  public doesEmailExist(username: string) {
    return this.http.get(this._apiEndpoint + username + '/');
  }

  public getUser(user_id: number) {
    return this.http.get<User>(this._apiEndpoint + user_id + '/');
  }

  public resendConfirmEmail(email: string) {
    const body = JSON.stringify({ email });
    return this.http.post(this._apiEndpoint + 'resend-confirm-email/', body);
  }

  public validateToken(token: string) {
    const body = JSON.stringify({ token });
    return this.http.post(this._apiEndpoint + 'validate_token/', body);
  }

  public sendResetPassword(email: string) {
    const body = JSON.stringify({ email });
    return this.http.post(this._apiEndpoint + 'send-reset-password/', body);
  }

  public getApplicationAnswers(application_id) {
    return this.http.get<Answer[]>(
      environment.apiUrl + 'jobs/answers/' + application_id + '/'
    );
  }

  public validateInvitation(unitId, body) {
    return this.http.post<{ token: string }>(
      environment.apiUrl +
        'units/' +
        unitId +
        '/employments/validate-invitation/',
      body
    );
  }

  /*
   * =================================
   *   Functions for regular users
   * =================================
   * */

  public getUserApplications() {
    return this.http.get<Application[]>(this._apiEndpoint + 'applications/');
  }

  public getIntercomHash() {
    return this.http.get(this._apiEndpoint + 'current-user/intercom-hash/');
  }
}
